import React, { useId, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Avatar,
  Divider,
  Stack,
  InputLabel,
  TextField,
  Popover,
  Typography,
} from '@mui/material';

function IconNameField({
  id,
  label,
  placeholderOne = '',
  placeholderTwo = '',
  icon,
  avatarVariant = 'rounded',
  showDivider = true,

  // extra props for each <TextField> if you need them
  firstProps = {},
  secondProps = {},

  // controlled values + change handlers
  firstName = '',
  lastName = '',
  firstNameChange = () => {},
  lastNameChange = () => {},

  // validation wiring
  errors = {},          // { firstName: 'msg', lastName: 'msg' }
  showErrors = false,   // set true after first failed submit
  submitTick = 0,       // increments on every submit click
  loading
}) {
  /* ---------- stable IDs ------------------------------------------------ */
  const autoId = useId();
  const baseId = id ?? `iconNameField-${autoId}`;

  // Use conventional keys so they match your errors object
  const idFirst = firstProps.id ?? 'firstName';
  const idLast  = secondProps.id ?? 'lastName';

  /* ---------- pick error messages -------------------------------------- */
  const errFirst = errors[idFirst];
  const errLast  = errors[idLast];

  const hasErrorFirst = showErrors && Boolean(errFirst);
  const hasErrorLast  = showErrors && Boolean(errLast);

  /* we’ll show only ONE popover – whichever field currently has an error   */
  const fieldError = errFirst || errLast;
  const hasError   = hasErrorFirst || hasErrorLast;

  /* ---------- refs & popover control ----------------------------------- */
  const refFirst  = useRef(null);
  const refLast   = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    // Choose the field that owns the current error
    const target =
      hasErrorFirst ? refFirst.current :
      hasErrorLast  ? refLast.current  :
      null;

    // Show popover unless the user is actively focused in that field
    if (target && document.activeElement !== target) {
      setAnchorEl(target);
    } else {
      setAnchorEl(null);
    }
  }, [hasErrorFirst, hasErrorLast, submitTick]);

  /* ---------- hide popover when user starts editing -------------------- */
  const handleFocusFirst = (e) => {
    setAnchorEl(null);
    firstProps.onFocus?.(e);
  };

  const handleFocusLast = (e) => {
    setAnchorEl(null);
    secondProps.onFocus?.(e);
  };

const widthInCh = (value, placeholder, min = 1) =>
  Math.max(value.length, placeholder.length, min) + 1;

  /* --------------------------------------------------------------------- */
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: 1,
        borderColor: '#C5D3DD',
        borderRadius: '16px',
        px: { xs: 2, sm: 3 },
        gap: { xs: 1.5, sm: 2.5 },
        height: "65px",
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Icon slot */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {typeof icon === 'string' ? (
          <Avatar src={icon} alt='' sx={{ width: 35, height: 35 }} variant={avatarVariant} />
        ) : (
          icon
        )}
      </Box>

      {/* Vertical divider */}
      {showDivider && (
        <Divider orientation='vertical' flexItem sx={{ mx: 0.5, borderColor: '#C5D3DD' }} />
      )}

      {/* Label + two inputs */}
      <Stack sx={{ flexGrow: 1, pt: 1, py: 1.5 }}>
        <InputLabel
          htmlFor={idFirst}
          sx={{
            fontFamily: "'Lato', sans-serif",
            fontWeight: 500,
            color: '#BCBCBC',
            fontSize: 14,
            lineHeight: 1,
            mb: 0.5,
            pt: 1,
          }}
        >
          {label}
        </InputLabel>

        {/* Two fields side-by-side */}
        <Stack direction='row' spacing={1} sx={{ width: '100%' }}>
          {/* First name */}
          <TextField
            id={idFirst}
            variant='standard'
            placeholder={firstProps.placeholder ?? placeholderOne}
            value={firstName}
            onChange={firstNameChange}
            error={hasErrorFirst}
            inputRef={refFirst}
            onFocus={handleFocusFirst}
             disabled={loading} 
            InputProps={{
              disableUnderline: true,
              ...(firstProps.InputProps || {}),
            }}
            inputProps={{
               style: { width: `${widthInCh(firstName, placeholderOne, 3)}ch` },
    sx: {
      fontSize: '1rem',
      fontWeight: 600,
      fontFamily: "'Lato', sans-serif",
      color: '#49768C',
      // optional - keeps the input from shrinking past the placeholder
      minWidth: `${placeholderOne.length + 1}ch`,
    },
              ...(firstProps.inputProps || {}),
            }}
            sx={{
    width: 'fit-content',                 // or 'auto'
    // but don’t let it ever explode
    maxWidth: '15ch',
    // DON’T need min-width here any more
    '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
  
            }}
            {...firstProps}
          />

          {/* Last name */}
          <TextField
            id={idLast}
            variant='standard'
            placeholder={secondProps.placeholder ?? placeholderTwo}
            value={lastName}
            onChange={lastNameChange}
            error={hasErrorLast}
            inputRef={refLast}
            onFocus={handleFocusLast}
             disabled={loading} 
            InputProps={{
              disableUnderline: true,
              ...(secondProps.InputProps || {}),
            }}
            inputProps={{
              sx: { fontSize: '1rem', fontWeight: 600, fontFamily: "'Lato', sans-serif", color: "#49768C", },
              ...(secondProps.inputProps || {}),
            }}
            sx={{
              flex: '0 0 calc(50% - 4px)',
              minWidth: 120,
              '& .MuiInput-underline:before, & .MuiInput-underline:after': { display: 'none' },
            }}
            {...secondProps}
          />
        </Stack>
      </Stack>

      {/* Popover for whichever field is invalid */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        disableAutoFocus
        disableEnforceFocus
        sx={{ pointerEvents: 'none' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        PaperProps={{
          sx: {
            bgcolor: '#AA5869',
            color: '#fff',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            maxWidth: 260,
          },
          elevation: 3,
        }}
      >
        <Typography variant='caption'sx={{fontFamily: "'Lato', sans-serif",}}>{fieldError}</Typography>
      </Popover>
    </Box>
  );
}

IconNameField.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  placeholderOne: PropTypes.string,
  placeholderTwo: PropTypes.string,
  firstProps: PropTypes.object,
  secondProps: PropTypes.object,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  avatarVariant: PropTypes.oneOf(['rounded', 'circular', 'square']),
  showDivider: PropTypes.bool,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  firstNameChange: PropTypes.func,
  lastNameChange: PropTypes.func,
  errors: PropTypes.object,
  showErrors: PropTypes.bool,
  submitTick: PropTypes.number,
};

export default IconNameField;